import { DEFAULT_REPORT_RANGE_MONTHS, formatReportRangeLabel } from "src/constants/reportRange";
import { isRangeWithinLimit } from "src/utils/report/reportRange.util";

import { validateDateRange } from "../../utils";

/** `maxRangeMonths` is the plan's report date range (`useReportRangeLimit`); 1 year when the plan sets none. */
const dateRangeValidation = (values, errors = {}, maxRangeMonths: number = DEFAULT_REPORT_RANGE_MONTHS) => {
  if (!values.fromDate) errors.fromDate = "Please select from date";
  if (!values.toDate) errors.toDate = "Please select to date";

  const dateErrors = validateDateRange({
    fieldName: "toDate",
    startDateKey: "fromDate",
    endDateKey: "toDate",
    startDate: values.fromDate,
    endDate: values.toDate,
    startErrorText: "From date cannot be after to date",
    endErrorText: "To date cannot be before from date",
  });

  if (dateErrors.fromDate) errors.fromDate = dateErrors.fromDate;
  if (dateErrors.toDate) errors.toDate = dateErrors.toDate;

  if (values.fromDate && values.toDate && !isRangeWithinLimit(values.fromDate, values.toDate, maxRangeMonths)) {
    const message = `Date range cannot exceed ${formatReportRangeLabel(maxRangeMonths)} on your plan`;
    errors.fromDate = message;
    errors.toDate = message;
  }

  return errors;
};

export const crmReportValidationRules = (values, maxRangeMonths?: number) => {
  let errors = {};

  if (!values.assigneeId) errors.assigneeId = "Please select an assignee";
  if (!values.statusId) errors.statusId = "Please select a status";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const projectReportValidation = (values, selectedProjects, selectedProjectTypes, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project.";
  if (selectedProjectTypes !== "all" && (!values.projectTypes || values.projectTypes.length === 0))
    errors.projectTypes = "Please select at least one type";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const workerReportValidation = (values, selectedProjects, selectedWorkers, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedWorkers !== "all" && (!values.workerIds || values.workerIds.length === 0))
    errors.workerIds = "Please select at least one worker";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project.";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const vendorReportValidation = (values, selectedVendors, selectedProjects, selectedUsers, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedVendors !== "all" && (!values.vendorIds || values.vendorIds.length === 0))
    errors.vendorIds = "Please select at least one vendor";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project";
  if (selectedUsers !== "all" && (!values.createdByIds || values.createdByIds.length === 0))
    errors.createdByIds = "Please select at least one user";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const expenseReportValidation = (values, selectedExpenses, selectedProjects, selectedUsers, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedExpenses !== "all" && (!values.categoryIds || values.categoryIds.length === 0))
    errors.categoryIds = "Please select at least one category";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project";
  if (selectedUsers !== "all" && (!values.createdByIds || values.createdByIds.length === 0))
    errors.createdByIds = "Please select at least one user";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const subcontractorReportValidation = (
  values,
  selectedSubcontractors,
  selectedProjects,
  selectedUsers,
  maxRangeMonths?: number,
) => {
  let errors = {};

  if (selectedSubcontractors !== "all" && (!values.subcontractorIds || values.subcontractorIds.length === 0))
    errors.subcontractorIds = "Please select at least one subcontractor";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project";
  if (selectedUsers !== "all" && (!values.createdByIds || values.createdByIds.length === 0))
    errors.createdByIds = "Please select at least one user";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const clientReportValidation = (values, selectedClients, selectedProjects, selectedUsers, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedClients !== "all" && (!values.clientIds || values.clientIds.length === 0))
    errors.clientIds = "Please select at least one client";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project";
  if (selectedUsers !== "all" && (!values.createdByIds || values.createdByIds.length === 0))
    errors.createdByIds = "Please select at least one user";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const userReportValidation = (values, selectedUsers, selectedProjects, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedUsers !== "all" && (!values.userIds || values.userIds.length === 0))
    errors.userIds = "Please select at least one user";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project.";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const materialReportValidation = (
  values,
  selectedMaterials,
  selectedVendors,
  selectedProjects,
  maxRangeMonths?: number,
) => {
  let errors = {};

  if (selectedMaterials !== "all" && (!values.materialIds || values.materialIds.length === 0))
    errors.materialIds = "Please select at least one material";
  if (selectedVendors !== "all" && (!values.vendorIds || values.vendorIds.length === 0))
    errors.vendorIds = "Please select at least one vendor";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project.";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const accountReportValidation = (
  values: {
    accountIds?: number[] | null;
    accountTypes?: string[] | null;
    projectIds?: number[] | null;
    fromDate: string;
    toDate: string;
  },
  selectedAccounts?: number | string,
  selectedAccountTypes?: number | string,
  selectedProjects?: number | string,
  maxRangeMonths?: number,
): Record<string, string> => {
  let errors: Record<string, string> = {};

  if (selectedAccounts !== "all" && (!values.accountIds || values.accountIds.length === 0))
    errors.accountIds = "Please select at least one account";
  if (selectedAccountTypes !== "all" && (!values.accountTypes || values.accountTypes.length === 0))
    errors.accountTypes = "Please select at least one account type";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};

export const toolReportValidation = (values, selectedTools, selectedProjects, maxRangeMonths?: number) => {
  let errors = {};

  if (selectedTools !== "all" && (!values.toolIds || values.toolIds.length === 0))
    errors.toolIds = "Please select at least one tool";
  if (selectedProjects !== "all" && (!values.projectIds || values.projectIds.length === 0))
    errors.projectIds = "Please select at least one project.";
  errors = dateRangeValidation(values, errors, maxRangeMonths);

  return errors;
};
